<template>
  <div class="voip-manager">
    <!-- <CButton v-if="!showPanel"  -->
    <CButton 
      :color="serviceDisabled ? 'secondary' : (canReceiveCalls ? 'success' : 'warning')"
      :variant="canReceiveCalls ? 'outline' : null" 
      @click="togglePanel" 
      class="voip-trigger-btn vertical-center flex-gap-1"
    >
      <CSpinner v-if="loading.status || loading.dnd" size="sm" />
      <CIcon v-else :name="serviceDisabled ? 'cil-ban' : (canReceiveCalls ? 'cil-headphones' : 'cil-airplane-mode')" />
      <span class="btn-text">{{ serviceDisabled ? 'VoIP Disabled' : (canReceiveCalls ? 'VoIP Active' : 'VoIP Inactive') }}</span>
    </CButton>

    <transition name="slide-panel">
      <div v-if="showPanel" class="voip-panel">
        <div class="panel-header">
          <div class="d-flex align-items-center">
            <CSpinner v-if="loading.status" size="sm" class="mr-2" />
            <CIcon v-else name="cil-headphones" class="mr-2" />
            <h6 class="mb-0">VoIP Status</h6>
          </div>
          <CButton color="light" variant="outline" size="sm" @click="togglePanel">
            <CIcon name="cil-x" />
          </CButton>
        </div>

        <div class="panel-body">
          <!-- Status Card -->
          <div class="status-card mb-3" :class="statusCardClass">
            <div class="status-icon">
              <CIcon :name="statusIcon" size="lg" />
            </div>
            <div class="status-content">
              <h6 class="mb-1">{{ statusTitle }}</h6>
              <small>{{ statusMessage }}</small>
            </div>
          </div>

          <!-- Extension Info -->
          <div v-if="userExtension" class="extension-info mb-3">
            <div class="info-header">
              <CIcon name="cil-headphones" class="mr-2" />
              <strong>Extension: {{ userExtension }}</strong>
            </div>
            <div class="extension-details">
              <div class="detail-item">
                <span class="label">Call Status:</span>
                <span class="value" :class="canReceiveCalls ? 'text-success' : 'text-danger'">
                  {{ canReceiveCalls ? 'Can receive calls' : 'Cannot receive calls' }}
                </span>
              </div>
              <div class="detail-item">
                <span class="label">Do Not Disturb:</span>
                <span class="value" :class="dndEnabled ? 'text-danger' : 'text-success'">
                  {{ dndEnabled ? 'On' : 'Off' }}
                </span>
              </div>
              <div class="detail-item">
                <span class="label">Last Updated:</span>
                <span class="value">{{ lastUpdated }}</span>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div v-if="!(serviceDisabled || userAccountDisabled) && userExtension" class="action-buttons">
            <CButton v-if="!dndEnabled" color="danger" :disabled="loading.dnd || !userExtension"
              @click="enableDND" block>
              <CSpinner v-if="loading.dnd" size="sm" class="mr-1" />
              <CIcon v-else name="cil-airplane-mode" class="mr-1" />
              Stop Receiving Calls
            </CButton>

            <CButton v-if="dndEnabled" color="success" :disabled="loading.dnd" @click="disableDND" block>
              <CSpinner v-if="loading.dnd" size="sm" class="mr-1" />
              <CIcon v-else name="cil-airplane-mode-off" class="mr-1" />
              Receive Calls
            </CButton>
          </div>

          <!-- VoIP Status Info -->
          <div class="voip-info mt-3">
            <div class="info-header">
              <CIcon name="cil-info" class="mr-2" />
              <strong>VoIP Call Status</strong>
            </div>
            <div class="info-content">
              <p class="mb-2">
                <strong>Current Status:</strong> 
                <span :class="canReceiveCalls ? 'text-success' : 'text-danger'">
                  {{ canReceiveCalls ? 'Available for calls' : 'Not available for calls' }}
                </span>
              </p>
              <ul class="mb-0">
                <li v-if="serviceDisabled">❌ VoIP service is disabled</li>
                <li v-else-if="!userExtension">❌ No VoIP extension assigned</li>
                <li v-else-if="userAccountDisabled">❌ VoIP account is unavailable</li>
                <li v-else-if="dndEnabled">❌ Do Not Disturb is on</li>
                <li v-else>✅ Ready to receive VoIP calls</li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: 'VoipManager',

  data() {
    return {
      showPanel: false,
      dndEnabled: false,
      lastUpdated: 'Never',

      loading: {
        dnd: false,
        status: false,
      },

      // Auto refresh interval
      refreshInterval: null,
    }
  },

  computed: {
    ...mapGetters(["errors", "voipEnabled", "voipAccountStatus", "voipIncomingCalls"]),

    serviceDisabled: {
      get() { return this.voipEnabled === false; },
      set(value) { this.$store.commit('setVoipEnabled', !value); }
    },
    userExtension() { return this.authUser?.user?.voip_extension; },
    userAccountDisabled: {
      get() { return [null, 'Unavailable'].includes(this.voipAccountStatus) },
      set(value) { this.$store.commit('setVoipAccountStatus', value || null); }
    },

    incomingCalls: {
      get() { return this.voipIncomingCalls; },
      set(value) { this.$store.commit('setVoipIncomingCalls', value || []); }
    },

    canReceiveCalls() {
      return !(
        this.serviceDisabled ||
        !this.userExtension ||
        this.userAccountDisabled ||
        this.dndEnabled
      )
    },

    statusCardClass() {
      if (this.serviceDisabled || this.userAccountDisabled) return 'status-danger';
      if (!this.userExtension) return 'status-warning';
      return this.canReceiveCalls ? 'status-success' : 'status-danger';
    },

    statusIcon() {
      if (this.serviceDisabled || this.userAccountDisabled) return 'cil-ban';
      if (!this.userExtension) return 'cil-warning';
      return this.canReceiveCalls ? 'cil-headphones' : 'cil-airplane-mode';
    },

    statusTitle() {
      if (this.serviceDisabled) return 'Service Disabled';
      if (!this.userExtension) return 'No VoIP Extension';
      if (this.userAccountDisabled) return 'VoIP Account Unavailable';
      return this.canReceiveCalls ? 'Available for Calls' : 'Not Available for Calls';
    },

    statusMessage() {
      if (this.serviceDisabled) return 'VoIP service is currently disabled by administrator.';
      if (!this.userExtension) return 'VoIP extension not assigned! Contact administrator to assign a VoIP extension for receiving calls.';
      if (this.userAccountDisabled) return `Your VoIP account related to extension ${this.userExtension} is unavailable!`;
      if (this.dndEnabled) {
        return `Extension ${this.userExtension} - Do Not Disturb is on`;
      }
      return `Extension ${this.userExtension} - Ready to receive calls`;
    },
  },

  async mounted() {
    // Only show for Order Admins
    if (!this.isOrderAdmin) return;

    await this.checkDNDStatus();

    // Auto refresh every 30 seconds
    this.refreshInterval = setInterval(() => {
      if (!this.serviceDisabled && this.userExtension) {
        this.checkDNDStatus();
      }
    }, 30000);
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },

  methods: {
    togglePanel() {
      this.showPanel = !this.showPanel;
      if (this.showPanel) {
        this.checkDNDStatus();
      }
    },

    async checkDNDStatus() {
      if (this.serviceDisabled || !this.userExtension) return;

      this.loading.status = true;

      try {
        const response = await this.$axios.get(this.$backend.VOIP.STATUS, {
          params: { extension: this.userExtension }
        });

        if (response.data.success) {
          this.dndEnabled = response.data.is_dnd_enabled;
          this.userAccountDisabled = response.data.account_status;
          this.incomingCalls = response.data.incoming_calls;
          this.lastUpdated = new Date().toLocaleTimeString();
          this.serviceDisabled = false;
        }
      } catch (error) {
        if (error?.response?.status === 503) {
          this.serviceDisabled = true;
        } else {
          this.$toast.error('Failed to check VoIP status');
        }
      } finally {
        this.loading.status = false;
      }
    },

    async enableDND() {
      if (!this.userExtension) return;

      this.$confirm({
        title: 'Enable Do Not Disturb',
        message: '⚠️ You will stop receiving VoIP calls. Are you sure?',
        button: { no: "Cancel", yes: "Enable DND" },
        callback: async (confirm) => {
          if (confirm) {
            await this.updateDNDStatus('yes');
          }
        }
      });
    },

    async disableDND() {
      if (!this.userExtension) return;

      await this.updateDNDStatus('no');
    },

    async updateDNDStatus(status) {
      this.loading.dnd = true;

      try {
        const response = await this.$axios.post(this.$backend.VOIP.EXTENSION.DND, {
          extension: this.userExtension,
          status: status
        });

        if (response.data.success) {
          this.dndEnabled = status === 'yes';
          this.lastUpdated = new Date().toLocaleTimeString();
          
          const message = status === 'yes' ? 'DND is on - Calls stopped' : 'DND is off - Calls resumed';
          this.$toast.success(message);
        } else {
          this.$toast.error(response.data.error || 'Failed to update VoIP status');
        }
      } catch (error) {
        this.$toast.error(error.response?.data?.error || 'Failed to update VoIP status');
      } finally {
        this.loading.dnd = false;
      }
    },
  }
}
</script>

<style scoped>
/* Responsive Trigger Button */
.voip-trigger-btn {
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.voip-trigger-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Button Text - Hidden on small screens */
.btn-text {
  white-space: nowrap;
}

/* Responsive Slide Panel */
.voip-panel {
  position: absolute;
  top: 45px;
  right: 0;
  width: 320px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  z-index: 1050;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.panel-body {
  padding: 1rem;
}

/* Status Card */
.status-card {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.status-success {
  background: #f0f9ff;
  border-left-color: #10b981;
}

.status-danger {
  background: #fef2f2;
  border-left-color: #ef4444;
}

.status-warning {
  background: #fffbeb;
  border-left-color: #f59e0b;
}

.status-icon {
  margin-right: 0.75rem;
  color: currentColor;
}

.status-content {
  flex: 1;
}

.status-content h6 {
  color: #1f2937;
  font-weight: 600;
}

.status-content small {
  color: #6b7280;
}

/* Extension Info */
.extension-info {
  background: #f8fafc;
  border-radius: 8px;
  padding: 0.75rem;
}

.info-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #374151;
}

.extension-details {
  background: white;
  border-radius: 6px;
  padding: 0.5rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.detail-item .label {
  font-weight: 500;
  color: #6b7280;
}

.detail-item .value {
  font-weight: 600;
}

/* VoIP Info */
.voip-info {
  background: #f8fafc;
  border-radius: 8px;
  padding: 0.75rem;
}

.info-content {
  background: white;
  border-radius: 6px;
  padding: 0.75rem;
  font-size: 0.875rem;
}

.info-content ul {
  padding-left: 1rem;
  margin-bottom: 0;
}

.info-content li {
  margin-bottom: 0.25rem;
  color: #6b7280;
}

/* Action Buttons */
.action-buttons {
  margin-top: 1rem;
}

.action-buttons .btn {
  border-radius: 8px;
  font-weight: 500;
}

/* Slide Panel Animation */
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: all 0.3s ease;
}

.slide-panel-enter,
.slide-panel-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

/* Responsive Design */
@media (max-width: 768px) {
  .voip-panel {
    width: 280px;
    right: -20px;
  }
  
  .btn-text {
    display: none;
  }
  
  .voip-trigger-btn {
    border-radius: 50% !important;
    width: 40px;
    height: 40px;
    padding: 0 !important;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .voip-panel {
    width: 260px;
    right: -30px;
  }
  
  .panel-body {
    padding: 0.75rem;
  }
  
  .status-card {
    padding: 0.5rem;
  }
  
  .extension-details {
    padding: 0.4rem;
  }
}

/* Pulsing animation for inactive state */
@keyframes pulse-warning {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
  }
}

.voip-trigger-btn.btn-warning {
  animation: pulse-warning 2s infinite;
}

/* Pulsing animation for unavailable state */
@keyframes pulse-danger {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
  }
}

.voip-trigger-btn.btn-danger {
  animation: pulse-danger 2s infinite;
}
</style>
